// Pure camera / projection math shared by the renderer and the viewmodel.

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

/** The aspect ratio the settings' horizontal FOV is defined at. */
export const REF_ASPECT = 16 / 9;

/**
 * Vertical FOV (degrees) for a horizontal FOV defined at 16:9. Wider screens keep the vertical FOV
 * (Hor+: they see more to the sides); narrower screens keep the horizontal FOV (they see more
 * vertically) so the sides are never cropped.
 */
export function verticalFovDeg(hFovDeg: number, aspect: number): number {
  const a = Math.min(Math.max(aspect, 0.25), REF_ASPECT);
  return 2 * Math.atan(Math.tan((hFovDeg * DEG) / 2) / a) * RAD;
}

/** Inverse of the above for a given aspect: horizontal FOV (degrees) of a vertical FOV. */
export function horizontalFovDeg(vFovDeg: number, aspect: number): number {
  return 2 * Math.atan(Math.tan((vFovDeg * DEG) / 2) * aspect) * RAD;
}

/**
 * Viewmodel vertical FOV. `settings.video.viewmodelFov` follows the Source convention: a horizontal
 * FOV at 4:3. The vertical FOV is constant across aspect ratios.
 */
export function viewmodelVerticalFovDeg(viewmodelFov: number): number {
  return 2 * Math.atan(Math.tan((viewmodelFov * DEG) / 2) * 0.75) * RAD;
}

/**
 * Internal render pixel ratio: device pixel ratio (capped at 2) times the render scale, reduced so
 * the drawing buffer never exceeds `maxPixels`.
 */
export function pixelRatioFor(
  renderScale: number,
  devicePixelRatio: number,
  cssWidth: number,
  cssHeight: number,
  maxPixels = 8_500_000,
): number {
  const scale = Math.min(Math.max(renderScale, 0.5), 1.5);
  let ratio = Math.min(Math.max(devicePixelRatio, 1), 2) * scale;
  const px = cssWidth * cssHeight * ratio * ratio;
  if (px > maxPixels) ratio *= Math.sqrt(maxPixels / px);
  return Math.max(ratio, 0.25);
}
